/**
 * 培训要素管理页面
 */

import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Popconfirm,
  Space,
  Alert,
  Row,
  Col,
  Statistic,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  ExportOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { TrainingElement, TrainingElementFormData, PaginationParams } from '../../types';
import { 
  getTrainingElements, 
  createTrainingElement, 
  updateTrainingElement, 
  deleteTrainingElement,
  getTrainingElementStats,
  deleteAllTrainingElements,
  exportTrainingElements
} from '../../services/api';
import { formatDateTime } from '../../utils/helpers';
import { exportTrainingElementsToExcel, showConfirmDialog } from '../../utils/exportUtils';

const { TextArea } = Input;

const TrainingElements: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [elements, setElements] = useState<TrainingElement[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState('');
  
  // 统计数据状态
  const [totalCount, setTotalCount] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // 模态框状态
  const [modalVisible, setModalVisible] = useState(false);
  const [editingElement, setEditingElement] = useState<TrainingElement | null>(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadElements();
    loadStats();
  }, [currentPage, pageSize, searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  // 加载培训要素列表
  const loadElements = async () => {
    try {
      setLoading(true);
      const params: PaginationParams = {
        page: currentPage,
        limit: pageSize,
        search: searchText || undefined,
      };
      
      const data = await getTrainingElements(params);
      setElements(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      console.error('加载培训要素列表失败:', err);
      message.error(err instanceof Error ? err.message : '加载培训要素列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载统计数据
  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const data = await getTrainingElementStats();
      setTotalCount(data.total);
    } catch (err) {
      console.error('加载统计数据失败:', err);
      message.error(err instanceof Error ? err.message : '加载统计数据失败');
    } finally {
      setStatsLoading(false);
    }
  };

  // 处理搜索
  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  // 打开新增模态框
  const handleAdd = () => {
    setEditingElement(null);
    setModalVisible(true);
    form.resetFields();
  };

  // 打开编辑模态框
  const handleEdit = (element: TrainingElement) => {
    setEditingElement(element);
    setModalVisible(true);
    form.setFieldsValue({
      name: element.name,
      unit: element.unit,
      description: element.description,
    });
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingElement) {
        // 编辑培训要素
        const formData: TrainingElementFormData = {
          name: values.name.trim(),
          unit: values.unit.trim(),
          description: values.description?.trim() || '',
        };
        await updateTrainingElement(editingElement.id, formData);
        message.success('编辑培训要素成功');
      } else {
        // 批量新增培训要素
        const elementNames = values.name.split('、').map((name: string) => name.trim()).filter(Boolean);
        
        if (elementNames.length === 0) {
          message.error('请输入至少一个培训要素名称');
          return;
        }

        let successCount = 0;
        let failedElements: string[] = [];

        for (const elementName of elementNames) {
          try {
            const formData: TrainingElementFormData = {
              name: elementName,
              unit: values.unit.trim(),
              description: values.description?.trim() || '',
            };
            
            await createTrainingElement(formData);
            successCount++;
          } catch (err) {
            console.error(`创建培训要素 ${elementName} 失败:`, err);
            failedElements.push(elementName);
          }
        }

        if (successCount > 0) {
          message.success(`成功创建 ${successCount} 个培训要素`);
          if (failedElements.length > 0) {
            message.warning(`以下培训要素创建失败: ${failedElements.join('、')}`);
          }
          setModalVisible(false);
          loadElements();
          loadStats();
        } else {
          message.error('所有培训要素创建失败，请检查输入');
        }
      }
    } catch (err) {
      console.error('提交失败:', err);
      if (err instanceof Error) {
        message.error(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 删除培训要素
  const handleDelete = async (element: TrainingElement) => {
    try {
      await deleteTrainingElement(element.id);
      message.success('删除培训要素成功');
      loadElements();
      loadStats();
    } catch (err) {
      console.error('删除失败:', err);
      message.error(err instanceof Error ? err.message : '删除失败');
    }
  };

  // 删除全部培训要素
  const handleDeleteAll = async () => {
    try {
      const confirmed = await showConfirmDialog(
        '确定要删除所有培训要素数据吗？\n此操作不可恢复，且如果有培训标准依赖这些要素，将无法删除。'
      );
      
      if (!confirmed) {
        return;
      }

      const result = await deleteAllTrainingElements();
      message.success(`已删除 ${result.deleted} 条培训要素记录`);
      loadElements();
      loadStats();
    } catch (err) {
      console.error('删除全部培训要素失败:', err);
      message.error(err instanceof Error ? err.message : '删除全部培训要素失败');
    }
  };

  // 导出培训要素数据
  const handleExport = async () => {
    try {
      const allElements = await exportTrainingElements();
      exportTrainingElementsToExcel(allElements, `培训要素数据_${new Date().toLocaleDateString()}.xlsx`);
      message.success('培训要素数据导出成功');
    } catch (err) {
      console.error('导出培训要素数据失败:', err);
      message.error(err instanceof Error ? err.message : '导出培训要素数据失败');
    }
  };

  // 获取单位标签颜色
  const getUnitTagColor = (unit: string) => {
    const unitColors: { [key: string]: string } = {
      '课时': 'blue',
      '天': 'green',
      '人次': 'orange',
      '场次': 'purple',
      '套': 'cyan',
      '份': 'magenta',
    };
    return unitColors[unit] || 'default';
  };

  // 表格列定义
  const columns: ColumnsType<TrainingElement> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '要素名称',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: '计量单位',
      dataIndex: 'unit',
      key: 'unit',
      width: 120,
      render: (unit: string) => (
        <Tag color={getUnitTagColor(unit)}>{unit}</Tag>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => formatDateTime(date),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record: TrainingElement) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个培训要素吗？"
            description="删除后相关的培训标准也将失效"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="培训要素总数"
              value={totalCount}
              loading={statsLoading}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        {/* 工具栏 */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              新增培训要素
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadElements();
                loadStats();
              }}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              导出数据
            </Button>
            <Popconfirm
              title="删除确认"
              description="确定要删除所有培训要素数据吗？此操作不可恢复！"
              onConfirm={handleDeleteAll}
              okText="确定"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<ClearOutlined />}
              >
                删除全部
              </Button>
            </Popconfirm>
            <Input.Search
              placeholder="搜索要素名称..."
              allowClear
              style={{ width: 300 }}
              onSearch={handleSearch}
            />
          </Space>
        </div>

        {/* 表格 */}
        <Table
          columns={columns}
          dataSource={elements}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条，共 ${total} 条记录`,
            onChange: (page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(1);
              }
            },
          }}
        />
      </Card>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingElement ? '编辑培训要素' : '新增培训要素'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        confirmLoading={submitting}
        width={600}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          {editingElement ? (
            // 编辑模式 - 单个要素
            <Form.Item
              name="name"
              label="要素名称"
              rules={[
                { required: true, message: '请输入要素名称' },
                { max: 100, message: '要素名称不能超过100个字符' },
              ]}
            >
              <Input placeholder="请输入培训要素名称" />
            </Form.Item>
          ) : (
            // 新增模式 - 批量输入
            <Form.Item
              name="name"
              label="批量添加培训要素"
              rules={[
                { required: true, message: '请输入要素名称' },
              ]}
            >
              <TextArea
                rows={4}
                placeholder="请输入培训要素名称，多个要素使用 、 分隔
例如：师资费用、场地费用、教材费用、设备租赁费用、餐饮费用"
                maxLength={1000}
                showCount
              />
            </Form.Item>
          )}

          <Form.Item
            name="unit"
            label={editingElement ? "计量单位" : "统一计量单位"}
            rules={[
              { required: true, message: '请输入计量单位' },
              { max: 20, message: '计量单位不能超过20个字符' },
            ]}
            tooltip={editingElement ? undefined : "批量添加时，所有要素将使用相同的计量单位"}
          >
            <Input placeholder="请输入计量单位，如：课时、天、人次、场次等" />
          </Form.Item>

          <Form.Item
            name="description"
            label="要素描述"
            rules={[
              { max: 500, message: '描述不能超过500个字符' },
            ]}
          >
            <TextArea
              rows={4}
              placeholder="请输入培训要素的详细描述（可选）"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>

        <Alert
          message={editingElement ? "编辑说明" : "批量添加说明"}
          description={
            editingElement ? (
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>编辑单个培训要素的基本信息</li>
                <li>要素名称和单位用于后续培训标准的关联配置</li>
              </ul>
            ) : (
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li><strong>批量输入：</strong>支持同时添加多个培训要素</li>
                <li><strong>分隔符：</strong>使用中文顿号 "、" 分隔多个要素名称</li>
                <li><strong>统一单位：</strong>批量添加时，所有要素共享相同的计量单位</li>
                <li><strong>常见单位：</strong>课时（师资费用）、天（场地费用）、人次（教材费用）、场次（设备费用）</li>
                <li><strong>要素描述：</strong>批量添加时，所有要素共享相同的描述</li>
                <li><strong>示例输入：</strong>师资费用、场地费用、教材费用、设备租赁费用、餐饮费用</li>
              </ul>
            )
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Modal>
    </div>
  );
};

export default TrainingElements;